import { useState, useMemo } from 'react';
import { demoBookings, demoGuests, demoRooms } from '@/mocks/demoData';
import { useApp } from '@/contexts/AppContext';
import { generateReceiptPDF } from '@/utils/pdfUtils';
import { getPayments, getPaymentsByBooking } from '@/utils/paymentStore';
import type { Payment, Booking } from '@/mocks/data';

const HOTEL_NAME = 'Grand Hotel Pro';
const HOTEL_ADDRESS = '123, MG Road, Bengaluru, Karnataka 560001';

const PAYMENT_METHODS = ['all', 'cash', 'card', 'upi', 'online'];
const PAYMENT_TYPES = ['all', 'partial', 'full', 'deposit'];

export default function ReceptionBilling() {
  const { addToast } = useApp();
  const [filterMethod, setFilterMethod] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);

  const filteredPayments = useMemo(() => {
    return getPayments().filter((p) => {
      const matchMethod = filterMethod === 'all' || p.payment_method === filterMethod;
      const matchType = filterType === 'all' || p.payment_type === filterType;
      const b = demoBookings.find((bk) => bk.id === p.booking_id);
      const g = b ? demoGuests.find((guest) => guest.id === b.guest_id) : null;
      const matchSearch =
        !searchQuery ||
        g?.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.receipt_no.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.booking_id.toLowerCase().includes(searchQuery.toLowerCase());
      return matchMethod && matchType && matchSearch;
    });
  }, [filterMethod, filterType, searchQuery]);

  const totals = useMemo(() => {
    return {
      total: filteredPayments.reduce((sum, p) => sum + p.amount, 0),
      cash: filteredPayments.filter((p) => p.payment_method === 'cash').reduce((sum, p) => sum + p.amount, 0),
      card: filteredPayments.filter((p) => p.payment_method === 'card').reduce((sum, p) => sum + p.amount, 0),
      upi: filteredPayments.filter((p) => p.payment_method === 'upi').reduce((sum, p) => sum + p.amount, 0),
      online: filteredPayments.filter((p) => p.payment_method === 'online').reduce((sum, p) => sum + p.amount, 0),
    };
  }, [filteredPayments]);

  const getGuest = (bookingId: string) => {
    const b = demoBookings.find((bk) => bk.id === bookingId);
    return b ? demoGuests.find((g) => g.id === b.guest_id) : null;
  };

  const getBookingId = (bookingId: string) => {
    const b = demoBookings.find((bk) => bk.id === bookingId);
    return b?.booking_id || bookingId;
  };

  const pendingBookings = useMemo(() => {
    return demoBookings.filter((b) => b.pending_amount > 0 && (b.status === 'checked_in' || b.status === 'confirmed'));
  }, []);

  const handleExport = () => {
    addToast('info', 'Exporting payment report...');
    setTimeout(() => addToast('success', 'Payment report exported successfully'), 1000);
  };

  const handleDownloadReceipt = (payment: Payment) => {
    const guest = getGuest(payment.booking_id);
    const bookingId = getBookingId(payment.booking_id);
    generateReceiptPDF(payment, guest?.full_name || 'Guest', bookingId);
    addToast('success', `Receipt ${payment.receipt_no} downloaded`);
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-navy-800">Billing & Receipts</h1>
          <p className="text-navy-500 mt-1">GST invoices, payment records, and receipt generation.</p>
        </div>
        <button
          onClick={handleExport}
          className="flex items-center gap-2 px-4 py-2.5 bg-navy-700 text-white rounded-lg hover:bg-navy-800 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-download-line" />
          Export Report
        </button>
      </div>

      {/* Payment Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-navy-100 p-4">
          <div className="text-xs text-navy-500 mb-1">Total</div>
          <div className="text-lg font-bold text-navy-800">Rs. {totals.total.toLocaleString('en-IN')}</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-4">
          <div className="text-xs text-navy-500 mb-1">Cash</div>
          <div className="text-lg font-bold text-green-600">Rs. {totals.cash.toLocaleString('en-IN')}</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-4">
          <div className="text-xs text-navy-500 mb-1">Card</div>
          <div className="text-lg font-bold text-blue-600">Rs. {totals.card.toLocaleString('en-IN')}</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-4">
          <div className="text-xs text-navy-500 mb-1">UPI</div>
          <div className="text-lg font-bold text-purple-600">Rs. {totals.upi.toLocaleString('en-IN')}</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-4">
          <div className="text-xs text-navy-500 mb-1">Online</div>
          <div className="text-lg font-bold text-orange-600">Rs. {totals.online.toLocaleString('en-IN')}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-navy-100 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              className="w-full px-3 py-2 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
              placeholder="Search guest, receipt, booking..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <select
            className="px-3 py-2 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
            value={filterMethod}
            onChange={(e) => setFilterMethod(e.target.value)}
          >
            {PAYMENT_METHODS.map((m) => (
              <option key={m} value={m}>{m === 'all' ? 'All Methods' : m.charAt(0).toUpperCase() + m.slice(1)}</option>
            ))}
          </select>
          <select
            className="px-3 py-2 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            {PAYMENT_TYPES.map((t) => (
              <option key={t} value={t}>{t === 'all' ? 'All Types' : t.charAt(0).toUpperCase() + t.slice(1)}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Payments Table */}
      <div className="bg-white rounded-xl border border-navy-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-navy-50">
              <tr>
                <th className="text-left px-5 py-3 font-semibold text-navy-700">Receipt No</th>
                <th className="text-left px-5 py-3 font-semibold text-navy-700">Guest</th>
                <th className="text-left px-5 py-3 font-semibold text-navy-700">Booking</th>
                <th className="text-left px-5 py-3 font-semibold text-navy-700">Date</th>
                <th className="text-left px-5 py-3 font-semibold text-navy-700">Method</th>
                <th className="text-left px-5 py-3 font-semibold text-navy-700">Type</th>
                <th className="text-right px-5 py-3 font-semibold text-navy-700">Amount</th>
                <th className="text-center px-5 py-3 font-semibold text-navy-700">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredPayments.map((p) => {
                const guest = getGuest(p.booking_id);
                const b = demoBookings.find((bk) => bk.id === p.booking_id);
                return (
                  <tr key={p.id} className="border-t border-navy-100 hover:bg-navy-50/50 transition-colors">
                    <td className="px-5 py-3.5 font-medium text-navy-800">{p.receipt_no}</td>
                    <td className="px-5 py-3.5 text-navy-600">{guest?.full_name || '-'}</td>
                    <td className="px-5 py-3.5 text-navy-500 text-xs">{b?.booking_id}</td>
                    <td className="px-5 py-3.5 text-navy-500 text-xs">
                      {new Date(p.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </td>
                    <td className="px-5 py-3.5">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        p.payment_method === 'cash' ? 'bg-green-50 text-green-700' :
                        p.payment_method === 'card' ? 'bg-blue-50 text-blue-700' :
                        p.payment_method === 'upi' ? 'bg-purple-50 text-purple-700' :
                        'bg-orange-50 text-orange-700'
                      }`}>
                        <i className={`${
                          p.payment_method === 'cash' ? 'ri-money-rupee-circle-line' :
                          p.payment_method === 'card' ? 'ri-bank-card-line' :
                          p.payment_method === 'upi' ? 'ri-smartphone-line' :
                          'ri-global-line'
                        }`} />
                        {p.payment_method.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                        p.payment_type === 'full' ? 'bg-green-50 text-green-700' :
                        p.payment_type === 'deposit' ? 'bg-amber-50 text-amber-700' :
                        'bg-blue-50 text-blue-700'
                      }`}>
                        {p.payment_type}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-right font-medium text-navy-800">Rs. {p.amount.toLocaleString('en-IN')}</td>
                    <td className="px-5 py-3.5 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => setSelectedPayment(p)}
                          className="text-navy-400 hover:text-navy-600 cursor-pointer"
                          title="View receipt"
                        >
                          <i className="ri-eye-line text-lg" />
                        </button>
                        <button
                          onClick={() => handleDownloadReceipt(p)}
                          className="text-navy-400 hover:text-green-600 cursor-pointer"
                          title="Download PDF receipt"
                        >
                          <i className="ri-file-download-line text-lg" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filteredPayments.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-5 py-12 text-center text-navy-400">
                    <i className="ri-search-line text-3xl mb-2 block" />
                    No payments found matching your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pending Payments */}
      {pendingBookings.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
              <i className="ri-time-line text-red-600 text-lg" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-navy-800">Pending Payments</h2>
              <p className="text-xs text-navy-500">Bookings with outstanding balance — collect payment at check-out or reception.</p>
            </div>
            <span className="ml-auto text-sm font-semibold text-red-700 bg-red-50 px-3 py-1 rounded-full">
              {pendingBookings.length} booking{pendingBookings.length > 1 ? 's' : ''}
            </span>
          </div>
          <div className="bg-white rounded-xl border border-red-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-red-50/50">
                  <tr>
                    <th className="text-left px-5 py-3 font-semibold text-navy-700">Guest</th>
                    <th className="text-left px-5 py-3 font-semibold text-navy-700">Booking ID</th>
                    <th className="text-left px-5 py-3 font-semibold text-navy-700">Room</th>
                    <th className="text-right px-5 py-3 font-semibold text-navy-700">Total</th>
                    <th className="text-right px-5 py-3 font-semibold text-navy-700">Paid</th>
                    <th className="text-right px-5 py-3 font-semibold text-navy-700">Pending</th>
                    <th className="text-left px-5 py-3 font-semibold text-navy-700">Payment Methods Used</th>
                    <th className="text-center px-5 py-3 font-semibold text-navy-700">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingBookings.map((b) => {
                    const guest = demoGuests.find((g) => g.id === b.guest_id);
                    const room = demoRooms.find((r) => r.id === b.room_id);
                    const bookingPayments = getPaymentsByBooking(b.id);
                    const totalPaid = bookingPayments.reduce((sum, p) => sum + p.amount, 0) + b.deposit;
                    const methods = [...new Set(bookingPayments.map((p) => p.payment_method))];
                    const effectivePending = Math.max(0, b.total_amount - totalPaid);
                    return (
                      <tr key={b.id} className="border-t border-red-50 hover:bg-red-50/30 transition-colors">
                        <td className="px-5 py-3.5">
                          <div className="font-medium text-navy-800">{guest?.full_name || 'Unknown'}</div>
                          <div className="text-xs text-navy-400">{guest?.phone}</div>
                        </td>
                        <td className="px-5 py-3.5 text-navy-600 text-xs font-mono">{b.booking_id}</td>
                        <td className="px-5 py-3.5 text-navy-600">{room?.room_number} ({room?.category})</td>
                        <td className="px-5 py-3.5 text-right font-medium text-navy-800">Rs. {b.total_amount.toLocaleString('en-IN')}</td>
                        <td className="px-5 py-3.5 text-right text-green-600 font-medium">Rs. {totalPaid.toLocaleString('en-IN')}</td>
                        <td className="px-5 py-3.5 text-right">
                          <span className="text-red-600 font-bold text-base">Rs. {effectivePending.toLocaleString('en-IN')}</span>
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex flex-wrap gap-1">
                            {methods.length > 0 ? methods.map((m) => (
                              <span key={m} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                m === 'cash' ? 'bg-green-50 text-green-700' :
                                m === 'card' ? 'bg-blue-50 text-blue-700' :
                                m === 'upi' ? 'bg-purple-50 text-purple-700' :
                                'bg-orange-50 text-orange-700'
                              }`}>
                                {m.toUpperCase()}
                              </span>
                            )) : (
                              <span className="text-xs text-navy-400">—</span>
                            )}
                          </div>
                        </td>
                        <td className="px-5 py-3.5 text-center">
                          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                            b.status === 'checked_in' ? 'bg-blue-50 text-blue-700' : 'bg-yellow-50 text-yellow-700'
                          }`}>
                            {b.status === 'checked_in' ? 'Checked In' : 'Confirmed'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Modal */}
      {selectedPayment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
            <div className="bg-navy-800 text-white px-6 py-5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-lg font-bold">{HOTEL_NAME}</div>
                  <div className="text-xs text-navy-300 mt-0.5">{HOTEL_ADDRESS}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-navy-300">Receipt</div>
                  <div className="font-bold">{selectedPayment.receipt_no}</div>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div className="flex justify-between border-b border-navy-100 pb-3">
                  <div>
                    <div className="text-xs text-navy-400">Guest</div>
                    <div className="font-medium text-navy-800">{getGuest(selectedPayment.booking_id)?.full_name || 'Guest'}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-navy-400">Date</div>
                    <div className="text-sm text-navy-700">{new Date(selectedPayment.created_at).toLocaleDateString('en-IN')}</div>
                  </div>
                </div>
                <div className="flex justify-between border-b border-navy-100 pb-3">
                  <div>
                    <div className="text-xs text-navy-400">Booking ID</div>
                    <div className="text-sm text-navy-700">{getBookingId(selectedPayment.booking_id)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-navy-400">Created By</div>
                    <div className="text-sm text-navy-700">{selectedPayment.created_by}</div>
                  </div>
                </div>
                <div className="flex justify-between items-center py-2">
                  <div>
                    <div className="text-xs text-navy-400">Payment Method</div>
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium mt-1 ${
                      selectedPayment.payment_method === 'cash' ? 'bg-green-50 text-green-700' :
                      selectedPayment.payment_method === 'card' ? 'bg-blue-50 text-blue-700' :
                      selectedPayment.payment_method === 'upi' ? 'bg-purple-50 text-purple-700' :
                      'bg-orange-50 text-orange-700'
                    }`}>
                      {selectedPayment.payment_method.toUpperCase()}
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-navy-400">Type</div>
                    <div className="text-sm font-medium text-navy-700 capitalize">{selectedPayment.payment_type}</div>
                  </div>
                </div>
                <div className="bg-navy-50 rounded-lg p-4 text-center">
                  <div className="text-xs text-navy-500 mb-1">Amount Received</div>
                  <div className="text-2xl font-bold text-navy-800">Rs. {selectedPayment.amount.toLocaleString('en-IN')}</div>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-navy-100 flex gap-3">
              <button
                onClick={() => {
                  handleDownloadReceipt(selectedPayment);
                }}
                className="flex-1 px-4 py-2.5 bg-navy-700 text-white rounded-lg text-sm font-medium hover:bg-navy-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-file-download-line mr-2" /> Download PDF
              </button>
              <button
                onClick={() => setSelectedPayment(null)}
                className="px-4 py-2.5 border border-navy-200 rounded-lg text-sm font-medium text-navy-600 hover:bg-navy-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}